import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Back to Home
          </Button>
        </Link>

        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Effective date: August 21, 2026</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Who we are</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Privacy Policy describes how TopServ Digital ("we," "our," or "us") handles information
              in connection with the Brand Equity Analyzer web application available at brandscore.biz (the
              "Service"). It applies to the Service only. If you also have a signed agreement with us, that
              agreement governs the services described in it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Information we collect</h2>

            <h3 className="text-xl font-semibold mb-3 mt-4">2.1 Information you submit</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When you start a brand analysis or create an account, we collect the fields the form asks for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>First name, last name, and (if you create an account) full name</li>
              <li>Email address</li>
              <li>Account password (stored only as a hash by our authentication provider)</li>
              <li>Business name, industry, business address, business phone number, and business description</li>
              <li>Website URL and any social media profile URLs you choose to provide</li>
              <li>Optional marketing consent choices you make on our forms</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Information generated by the Service</h3>
            <p className="text-muted-foreground leading-relaxed">
              The Service produces brand analysis reports, scores, score breakdowns, and report history
              associated with the business you submit. Reports are stored so that you can return to them.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-6">2.3 Publicly available business data</h3>
            <p className="text-muted-foreground leading-relaxed">
              To produce a report, we retrieve publicly available information about the business you specify —
              for example website content and performance metrics, search visibility data, review data, and
              public social media profile statistics. We do not ask you to submit information about other
              individuals, and you should not submit personal information about third parties through the Service.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-6">2.4 Technical and local storage data</h3>
            <p className="text-muted-foreground leading-relaxed">
              Our hosting provider processes standard server request data (such as IP address and user agent)
              in order to deliver and secure the site. The Service also stores information in your browser's
              local storage to keep you signed in and to carry your form progress between steps. See our{" "}
              <Link to="/cookies" className="underline text-brand-700">Cookie Policy</Link> for details. We do
              not run advertising or cross-site tracking technology on the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Why we use this information</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>To create and maintain your account and keep you signed in</li>
              <li>To run the brand analysis you requested and generate your report</li>
              <li>To store and display your report history</li>
              <li>To respond to your questions and provide support</li>
              <li>To send you marketing email or text messages, only where you have given separate consent</li>
              <li>To protect the Service against abuse, spam, and automated submissions</li>
              <li>To operate, troubleshoot, and improve the Service</li>
              <li>To comply with applicable law and enforce our Terms of Use</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Service providers and integrations</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use the following categories of processors and third-party services. They receive only the
              data needed for their function:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li><strong>Application hosting:</strong> Lovable (site hosting and delivery).</li>
              <li><strong>Backend, database, authentication, and serverless functions:</strong> Supabase.</li>
              <li><strong>CRM and messaging automation:</strong> LeadConnector / HighLevel, which receives the
                contact and business details you submit on the analysis form so we can follow up.</li>
              <li><strong>Bot protection:</strong> Cloudflare Turnstile (server-side verification) alongside an
                in-app challenge.</li>
              <li><strong>Analysis and enrichment data sources</strong> used to build reports, which receive the
                business identifiers being analyzed (such as business name, address, or website URL):
                Google (Places, Maps, PageSpeed Insights, Analytics Data where you connect it, YouTube),
                SerpApi, SEMrush, Perplexity, ScrapingBee / ScraperAPI, Yelp, Trustpilot data retrieval,
                X (Twitter), Facebook, and GitHub public APIs.</li>
              <li><strong>AI processing:</strong> Lovable AI Gateway, used to summarize and structure analysis
                output.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We may also disclose information to comply with law or legal process, to protect our rights, or in
              connection with a merger, financing, or sale of assets. Each provider handles the data it receives
              under its own terms and privacy notice, which we do not control.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We keep account records, business profiles, and generated reports for as long as your account is
              active, and afterwards only as long as needed for the purposes described here or as required by
              law. If you use the Service as a guest, the business details you enter are held in your own
              browser's local storage and are removed when you exit guest mode or clear your browser storage.
              Marketing contact records are kept until you unsubscribe or ask us to delete them, after which we
              keep only the record of your opt-out so that we can continue to honor it. We do not publish fixed
              retention periods; how long we keep something depends on the purpose it was collected for, whether
              your account is still active, and any legal obligation that applies. You can ask us to delete your
              data using the contact details in Section 9.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use reasonable administrative and technical safeguards, including HTTPS in transit,
              database-level access rules, server-side input validation, credentials held as server-side
              secrets, and bot protection on public forms. No method of transmission or storage is completely
              secure, and we cannot guarantee absolute security. Please do not submit sensitive personal
              information (such as health, financial account, or government ID data) through the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Children</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service is intended for businesses and for people aged 18 or older. It is not directed to
              children, and we do not knowingly collect personal information from anyone under 13. If you
              believe a child has provided us information, contact us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Your privacy choices and rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Depending on where you live — including under U.S. state privacy laws such as those in
              California, Colorado, Connecticut, Virginia, Texas, Oregon, and other states with comparable
              laws — you may have the right to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>Know and access the personal information we hold about you</li>
              <li>Request correction of inaccurate personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Obtain a portable copy of information you provided to us</li>
              <li>Opt out of targeted advertising, and of any sale or sharing of personal information</li>
              <li>Not be discriminated against for exercising these rights</li>
              <li>Appeal a decision we make about your request</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>How to submit a request:</strong> email{" "}
              <a href="mailto:privacy@topservdigital.com" className="underline text-brand-700">
                privacy@topservdigital.com
              </a>{" "}
              with the subject line "Privacy Request" and tell us which right you want to exercise. We will
              verify your request using the email address associated with your submission or account, and we
              may ask for additional information if we cannot verify you. An authorized agent may submit a
              request on your behalf with written permission. We respond within the timeframe required by
              applicable law.
            </p>
            <h3 className="text-xl font-semibold mb-3 mt-6">Sale and sharing of personal information</h3>
            <p className="text-muted-foreground leading-relaxed">
              <strong>We do not sell your personal information for money.</strong> We also do not load
              advertising technology, advertising pixels, or cross-site tracking tags on the Service, and we do
              not use your information for cross-context behavioral advertising.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We do want to be straightforward about one thing rather than making a blanket denial: some U.S.
              state privacy laws define "sale" and "sharing" broadly enough to cover disclosures made for
              something other than money. To operate the Service we pass information to the providers listed in
              Section 4 — for example your contact and business details to our CRM and messaging platform, and
              the business identifiers being analyzed, along with related technical or internet activity
              information, to our analysis and enrichment data sources. Depending on how those disclosures are
              characterized under a given state's law, some of them could be treated as a "sale" or "sharing"
              even though we receive no money for them. We therefore offer an opt-out rather than telling you
              the question cannot arise.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>How to opt out:</strong> use the <em>Cookie Settings</em> link in the site footer to record
              an opt-out of sale or sharing on your browser, or email us using the address above. We also treat a
              Global Privacy Control (GPC) browser signal as an opt-out request, and the site applies that signal
              automatically when your browser sends it. Separately, you can withdraw marketing consent at any
              time using the unsubscribe link in our emails, by replying STOP to a text message, or by emailing
              us. Withdrawing marketing consent or opting out does not stop us from providing a report you
              requested.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Contact us</h2>
            <p className="text-muted-foreground leading-relaxed">
              Questions about this policy or your data:{" "}
              <a href="mailto:privacy@topservdigital.com" className="underline text-brand-700">
                privacy@topservdigital.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Changes to this policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy. When we do, we will change the effective date above and post
              the updated version on this page. If the changes are material, we will provide additional notice
              in the Service or by email where appropriate.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
